import { FindingType_Enumerated, FindingType_Numeric } from '@app/advisor-shared';
import { BadImplementationException, exists, filterDuplicates, filterInstances, StaticLogger } from '@nu-art/ts-common';
import { ModuleFE_Advisor_Expression, ModuleFE_Advisor_Value, ModuleFE_Advisor_Variable } from '@app/advisor-frontend/modules/static-modules/healthcare-space';
import { valueType_Enumerated, valueType_Range, valueType_StandardRange, valueType_Tuple, variableType_AttributeInstance } from '@app/hcs-shared';
import { ModuleFE_RuntimeFinding } from '@app/advisor-frontend';
import { ModuleFE_SuggestedFinding } from '@app/advisor-frontend/modules/ModuleFE_SuggestedFinding';
//######################### ProcessedRawFinding Conversion #########################
const convert_NeededVariable_ProcessedRawFindingAttribute = (neededVariable) => {
    return {
        type: 'enumerated',
        variableId: neededVariable.variableId,
    };
};
const convert_NeededVariable_ProcessedRawFinding = (neededVariable) => {
    const variable = ModuleFE_Advisor_Variable.cache.unique(neededVariable.variableId);
    const value = ModuleFE_Advisor_Value.cache.unique(variable?.valueId);
    switch (value?.type) {
        case valueType_Tuple:
        case valueType_Enumerated: {
            return {
                type: 'enumerated',
                variableId: neededVariable.variableId,
                isChiefComplaint: false,
                attributes: filterInstances(neededVariable.attributes?.map(att => convert_NeededVariable_ProcessedRawFindingAttribute(att))),
            };
        }
        case valueType_Range:
        case valueType_StandardRange: {
            return {
                type: 'numeric',
                variableId: neededVariable.variableId,
                isChiefComplaint: false,
                measurementUnitId: value.data.unitsId
            };
        }
    }
};
//######################### Finding Conversion #########################
const convert_EnumFinding_FindingToUpdate = (neededVariable, finding) => {
    return {
        type: FindingType_Enumerated,
        findingId: finding._id,
        variableId: finding.variableId,
        isChiefComplaint: finding.isChiefComplaint,
        valueIds: finding.valueIds,
        parentId: finding.parentId,
        snippetId: finding.snippetId,
        attributes: filterInstances(finding.attributes?.map(att => {
            const processed = convert_NeededVariable_ProcessedRawFindingAttribute(att);
            processed.valueIds = att.valueIds;
            return processed;
        }))
    };
};
const convert_NumericFinding_FindingToUpdate = (finding) => {
    return {
        type: FindingType_Numeric,
        findingId: finding._id,
        variableId: finding.variableId,
        isChiefComplaint: finding.isChiefComplaint,
        value: finding.value,
        measurementUnitId: finding.measurementUnitId,
        parentId: finding.parentId,
        snippetId: finding.snippetId,
    };
};
const convert_Finding_FindingToUpdate = (neededVariable, finding) => {
    switch (finding.type) {
        case FindingType_Enumerated:
            return convert_EnumFinding_FindingToUpdate(neededVariable, finding);
        case FindingType_Numeric:
            return convert_NumericFinding_FindingToUpdate(finding);
        default:
            return;
    }
};
//######################### Provide Info Value Space Mapping #########################
const mapNeededVariable_ToUpdate = (neededVariable, previousContextFindings) => {
    //Assert the needed variable has a findingId
    if (!neededVariable.findingId)
        throw new BadImplementationException('Send neededVariable without findingId to mapNeededVariable_ToUpdate');
    const existing = previousContextFindings?.toUpdate.find(item => !item.isPartialCalculation && item.findingId === neededVariable.findingId);
    if (existing) //Return existing toUpdate item from previous context
        return existing;
    //Continue to create the finding to update
    const finding = ModuleFE_RuntimeFinding.cache.unique(neededVariable.findingId);
    if (!finding) {
        StaticLogger.logError('mapNeededVariable_ToUpdate', 'NeededVariable points to a non-existing finding', neededVariable);
        return;
    }
    return convert_Finding_FindingToUpdate(neededVariable, finding);
};
const mapNeededVariable_ToCreate = (neededVariable, previousContextFindings) => {
    //Assert the needed variable does not have a findingId
    if (neededVariable.findingId)
        throw new BadImplementationException('Send neededVariable with findingId to mapNeededVariable_ToCreate');
    const existing = previousContextFindings?.toCreate.find(item => item.variableId === neededVariable.variableId);
    if (existing) //Return existing toCreate item from previous context
        return existing;
    //Continue to create the finding to create
    const prf = convert_NeededVariable_ProcessedRawFinding(neededVariable);
    if (!prf)
        return;
    //Attach suggested finding
    const suggestedFinding = ModuleFE_SuggestedFinding.getForVariableId(neededVariable.variableId);
    if (suggestedFinding) {
        if (suggestedFinding.type !== prf.type)
            StaticLogger.logError('mapNeededVariable_ToCreate', 'SuggestedFinding type mismatch', `Calculated finding type: ${prf.type}`, `SuggestedFinding type: ${suggestedFinding.type}`);
        else
            prf.suggestedFinding = { finding: suggestedFinding, viewed: false, userInteracted: false };
    }
    //Return the finding to create
    return prf;
};
export const mapProvideInfoValueSpace = (neededVariables, previousContextFindings, topLevel = true) => {
    const provideInfoFindings = {
        toCreate: [],
        toUpdate: [],
    };
    //Map each needed variable into a finding, either to create or to update
    neededVariables.forEach(neededVariable => {
        if (neededVariable.findingId) { //Map into toUpdate
            const toUpdate = mapNeededVariable_ToUpdate(neededVariable, previousContextFindings);
            if (toUpdate)
                provideInfoFindings.toUpdate.push({ ...toUpdate });
        }
        else { //Map into toCreate
            const toCreate = mapNeededVariable_ToCreate(neededVariable, previousContextFindings);
            if (toCreate) {
                if (topLevel && exists(toCreate.suggestedFinding)) //Mark suggested finding as viewed
                    toCreate.suggestedFinding.viewed = true;
                provideInfoFindings.toCreate.push({ ...toCreate });
            }
        }
        //Map inner needed variables
        if (neededVariable.innerVariables?.length) {
            const innerFindings = mapProvideInfoValueSpace(neededVariable.innerVariables, previousContextFindings, false);
            provideInfoFindings.toCreate.push(...innerFindings.toCreate);
            provideInfoFindings.toUpdate.push(...innerFindings.toUpdate);
        }
    });
    //Return space
    return provideInfoFindings;
};
export const getExpressionTermVariableIds = (expression) => {
    const ids = [];
    expression.terms.forEach(term => {
        term.atoms.forEach(atom => {
            switch (atom.leftHand?.type) {
                case 'variable':
                    ids.push(atom.leftHand.id);
                    break;
                case 'expression': {
                    const nested = ModuleFE_Advisor_Expression.cache.unique(atom.leftHand.id);
                    if (nested?.associatedVar)
                        ids.push(nested.associatedVar);
                    break;
                }
            }
        });
    });
    return filterDuplicates(ids);
};
export const supplementExpressionVariablesInProvideInfoFindings = (currentFindings, expressions) => {
    const existingIds = new Set();
    const variableIds = new Set();
    //Collect initial existing ids
    [...currentFindings.toUpdate, ...currentFindings.toCreate].forEach(item => {
        existingIds.add(item.variableId);
        if ('attributes' in item)
            item.attributes?.forEach(att => existingIds.add(att.variableId));
    });
    expressions.forEach(exp => {
        getExpressionTermVariableIds(exp).forEach(id => {
            if (!existingIds.has(id))
                variableIds.add(id);
        });
    });
    //Add findings to create from variable ids
    variableIds.forEach(id => {
        const variable = ModuleFE_Advisor_Variable.cache.unique(id);
        if (!variable || variable.type === variableType_AttributeInstance)
            return;
        const prf = convert_NeededVariable_ProcessedRawFinding({ variableId: variable._id });
        if (prf)
            currentFindings.toCreate.push(prf);
    });
};
//# sourceMappingURL=finding-mapping.js.map